<template>
  <div class="pa-4">
    <breadcrumb />

    <div class="flex-wrap mb-4 d-flex align-center justify-space-between">
      <div class="d-flex align-center">
        <h2 class="mr-3 black--text">{{ $t("reports.cropProgression.reportComparison") }}</h2>
        <v-btn small rounded color="primary" depressed @click="showGuide = true">
          <v-icon left small>mdi-help-circle-outline</v-icon>
          {{ $t("reports.cropProgression.aiReportGuide") }}
        </v-btn>
      </div>
      <div>
        <v-btn
          color="primary"
          depressed
          class="mr-2"
          :disabled="!canDownload"
          :loading="downloading"
          @click="downloadComparison"
        >
          {{ $t("reports.cropProgression.downloadReport") }}
        </v-btn>
        <v-btn outlined color="primary" @click="goBack">{{ $t("reports.cropProgression.back") }}</v-btn>
      </div>
    </div>

    <v-card class="pa-4" outlined v-if="baseReport">
      <!-- Meta -->
      <h3 class="mb-3 black--text">{{ baseReport.farmName ? baseReport.farmName + " Report" : $t("reports.cropProgression.title") }}</h3>
      <v-row class="mb-2">
        <v-col cols="6" md="3">
          <div class="grey--text text-caption">{{ $t("reports.cropProgression.dateOfReport") }}</div>
          <div class="font-weight-bold">{{ baseReport.createdAt | formatDate }}</div>
        </v-col>
        <v-col cols="6" md="3">
          <div class="grey--text text-caption">{{ $t("reports.cropProgression.farm") }}</div>
          <div class="font-weight-bold">{{ baseReport.farmName || "-" }}</div>
        </v-col>
        <v-col cols="6" md="3">
          <div class="grey--text text-caption">{{ $t("reports.cropProgression.zone") }}</div>
          <div class="font-weight-bold">{{ baseReport.zoneName || "-" }}</div>
        </v-col>
        <v-col cols="6" md="3">
          <div class="grey--text text-caption">{{ $t("reports.cropProgression.location") }}</div>
          <div class="font-weight-bold">{{ baseReport.locationName || "-" }}</div>
        </v-col>
      </v-row>

      <v-divider class="my-3" />

      <h3 class="mb-2 black--text">{{ $t("reports.cropProgression.imageComparison") }}</h3>
      <v-tabs v-model="tab" class="mb-4">
        <v-tab>{{ $t("reports.cropProgression.sideComparison") }}</v-tab>
        <v-tab>{{ $t("reports.cropProgression.sliderComparison") }}</v-tab>
        <v-tab>{{ $t("reports.cropProgression.transparencyComparison") }}</v-tab>
      </v-tabs>

      <!-- Date selectors -->
      <v-row>
        <v-col cols="12" md="6">
          <label class="grey--text text-caption">{{ $t("reports.cropProgression.selectDate") }}</label>
          <v-select v-model="dateA" :items="dateItems" outlined dense hide-details :loading="loadingA" />
        </v-col>
        <v-col cols="12" md="6">
          <label class="grey--text text-caption">{{ $t("reports.cropProgression.selectDate") }}</label>
          <v-select v-model="dateB" :items="dateItems" outlined dense hide-details :loading="loadingB" />
        </v-col>
      </v-row>

      <!-- Side by side -->
      <div v-if="tab === 0" class="mt-4">
        <v-row>
          <v-col cols="12" md="6">
            <div class="map-holder">
              <div v-if="loadingAActive" class="map-loading">
                <v-progress-circular indeterminate color="primary" size="36" />
                <div v-if="aGenerating" class="mt-2 grey--text">{{ $t("reports.cropProgression.generating") }}</div>
              </div>
              <index-overlay-map :overlays="overlaysA" :outline-coordinates="outline" :circle="circleOutline" height="460px" />
            </div>
          </v-col>
          <v-col cols="12" md="6">
            <div class="map-holder">
              <div v-if="loadingBActive" class="map-loading">
                <v-progress-circular indeterminate color="primary" size="36" />
                <div v-if="bGenerating" class="mt-2 grey--text">{{ $t("reports.cropProgression.generating") }}</div>
              </div>
              <index-overlay-map :overlays="overlaysB" :outline-coordinates="outline" :circle="circleOutline" height="460px" />
            </div>
          </v-col>
        </v-row>
      </div>

      <!-- Slider -->
      <div v-else-if="tab === 1" class="mt-4 slider-wrap map-holder" ref="sliderWrap">
        <div v-if="comboBusy" class="map-loading">
          <v-progress-circular indeterminate color="primary" size="36" />
          <div v-if="comboGenerating" class="mt-2 grey--text">{{ $t("reports.cropProgression.generating") }}</div>
        </div>
        <index-overlay-map
          :overlays="sliderOverlays"
          :outline-coordinates="outline"
          :circle="circleOutline"
          height="520px"
          @overlay-rects="onOverlayRects"
        />
        <div v-if="overlayRect && !comboBusy" class="slider-divider" :style="dividerStyle">
          <div class="slider-handle" @mousedown="startDrag"><v-icon small color="black">mdi-arrow-left-right</v-icon></div>
        </div>
      </div>

      <!-- Transparency -->
      <div v-else class="mt-4">
        <div class="map-holder">
          <div v-if="comboBusy" class="map-loading">
            <v-progress-circular indeterminate color="primary" size="36" />
            <div v-if="comboGenerating" class="mt-2 grey--text">{{ $t("reports.cropProgression.generating") }}</div>
          </div>
          <index-overlay-map :overlays="transparencyOverlays" :outline-coordinates="outline" :circle="circleOutline" height="520px" />
        </div>
        <transparency-slider
          v-model="opacity"
          :label="$t('reports.cropProgression.transparencySlider')"
          class="mt-3"
        />
      </div>

      <v-divider class="my-4" />

      <!-- Index selector + legend -->
      <h4 class="mb-1 primary--text">{{ $t("reports.cropProgression.reportType") }}</h4>
      <v-select
        v-model="selectedIndex"
        :items="indexItems"
        :label="$t('reports.cropProgression.selectReportType')"
        outlined
        dense
        hide-details
        style="max-width: 420px"
        class="mb-4"
      />
      <index-legend :index-type="selectedIndex" :metadata="metadata" :label="indexLabel(selectedIndex)" />

      <v-alert type="warning" text class="mt-4" border="left" icon="mdi-alert">
        <strong>{{ $t("reports.cropProgression.disclaimerTitle") }}</strong><br />
        {{ $t("reports.cropProgression.disclaimer") }}
      </v-alert>
    </v-card>

    <guide-dialog :value="showGuide" @input="showGuide = $event" :metadata="metadata" />

    <confirm-box
      v-if="generatePrompt"
      :dialog="generatePrompt"
      :title="$t('reports.cropProgression.selectDate')"
      :message="$t('reports.cropProgression.notGeneratedPrompt')"
      :confirm-btn-text="$t('reports.cropProgression.generateNow')"
      :cancel-btn-text="$t('reports.cropProgression.cancel')"
      @continue:action="confirmGenerate"
      @cancel:action="cancelGenerate"
    />
  </div>
</template>

<script>
import CropProgressionService from "@/_services/CropProgressionService";
import IndexOverlayMap from "./components/IndexOverlayMap.vue";
import IndexLegend from "./components/IndexLegend.vue";
import GuideDialog from "./components/GuideDialog.vue";
import TransparencySlider from "../shared/TransparencySlider.vue";
import ConfirmBox from "@/components/ConfirmBox.vue";
import loadingMixin from "@/mixins/LoadingMixin";
import downloadMixin from "@/mixins/DownloadMixin";
import reportError from "@/mixins/reportError";

const INDICES = ["NDVI", "MSAVI", "LSWI", "RECI", "NDRE"];
const POLL_MS = 4000;

export default {
  name: "CropProgressionComparison",
  components: { IndexOverlayMap, IndexLegend, GuideDialog, TransparencySlider, ConfirmBox },
  mixins: [loadingMixin, downloadMixin, reportError],
  data() {
    return {
      baseReport: null,
      metadata: {},
      dates: [],
      generatedMap: {},
      tab: 0,
      dateA: null,
      dateB: null,
      prevDateA: null,
      prevDateB: null,
      reportA: null,
      reportB: null,
      loadingA: false,
      loadingB: false,
      selectedIndex: "NDVI",
      opacity: 50,
      sliderPct: 50,
      overlayRect: null,
      showGuide: false,
      generatePrompt: false,
      pendingSlot: null,
      pendingDate: null,
      dragging: false,
      downloading: false,
      pollTimers: {},
    };
  },
  computed: {
    reportUid() {
      return this.$route.params.reportUid;
    },
    dateItems() {
      return (this.dates || []).map((d) => ({ text: this.$options.filters.formatDate(d), value: d }));
    },
    indexItems() {
      return INDICES.map((i) => ({ text: this.indexLabel(i), value: i }));
    },
    outline() {
      return (this.baseReport && this.baseReport.coordinates) || [];
    },
    circleOutline() {
      if (this.baseReport && this.baseReport.radius) {
        return { lat: this.baseReport.centerLatitude, lng: this.baseReport.centerLongitude, radiusKm: this.baseReport.radius };
      }
      return null;
    },
    overlaysA() {
      return this.singleOverlay(this.reportA);
    },
    overlaysB() {
      return this.singleOverlay(this.reportB);
    },
    sliderOverlays() {
      const a = this.imgOf(this.reportA);
      const b = this.imgOf(this.reportB);
      const out = [];
      // B is the base layer (revealed on the right); A sits on top clipped to the
      // left of the divider, so report A always reads on the left and B on the right.
      if (b) out.push({ url: b.url, bbox: b.bbox, opacity: 1, clipRightPct: null });
      if (a) out.push({ url: a.url, bbox: a.bbox, opacity: 1, clipRightPct: b ? this.sliderPct : null });
      return out;
    },
    transparencyOverlays() {
      const a = this.imgOf(this.reportA);
      const b = this.imgOf(this.reportB);
      const out = [];
      if (a) out.push({ url: a.url, bbox: a.bbox, opacity: 1, clipRightPct: null });
      if (b) out.push({ url: b.url, bbox: b.bbox, opacity: this.opacity / 100, clipRightPct: null });
      return out;
    },
    // A slot is "busy" while its report is being fetched OR still generating.
    aGenerating() {
      return !!(this.reportA && this.reportA.status === "GENERATING");
    },
    bGenerating() {
      return !!(this.reportB && this.reportB.status === "GENERATING");
    },
    loadingAActive() {
      return this.loadingA || this.aGenerating;
    },
    loadingBActive() {
      return this.loadingB || this.bGenerating;
    },
    // Slider/transparency use one combined map for both dates.
    comboBusy() {
      return this.loadingAActive || this.loadingBActive;
    },
    comboGenerating() {
      return this.aGenerating || this.bGenerating;
    },
    // Both reports must be generated (COMPLETED/PARTIAL) to export a comparison.
    canDownload() {
      const ok = (r) => r && r.reportUid && ["COMPLETED", "PARTIAL"].includes(r.status);
      return ok(this.reportA) && ok(this.reportB) && !this.downloading;
    },
    // Divider is positioned over the raster image extent (not the whole map):
    // left tracks sliderPct across the image width; the line spans the image height.
    dividerStyle() {
      if (!this.overlayRect) return {};
      const left = this.overlayRect.left + (this.sliderPct / 100) * this.overlayRect.width;
      return {
        left: left + "px",
      };
    },
  },
  watch: {
    dateA(val, old) {
      this.prevDateA = old;
      this.onDateChange("A", val);
    },
    dateB(val, old) {
      this.prevDateB = old;
      this.onDateChange("B", val);
    },
  },
  created() {
    this.loadMetadata();
    this.init();
  },
  beforeDestroy() {
    Object.values(this.pollTimers).forEach((t) => clearInterval(t));
    window.removeEventListener("mousemove", this.onDrag);
    window.removeEventListener("mouseup", this.stopDrag);
  },
  methods: {
    indexLabel(i) {
      return this.$t(`reports.cropProgression.index${i}`);
    },
    imgOf(report) {
      if (!report || !report.bbox || !report.indices) return null;
      const idx = report.indices.find((x) => x.indexType === this.selectedIndex);
      if (idx && idx.url) return { url: idx.url, bbox: report.bbox };
      return null;
    },
    singleOverlay(report) {
      const img = this.imgOf(report);
      return img ? [{ url: img.url, bbox: img.bbox, opacity: 1, clipRightPct: null }] : [];
    },
    async loadMetadata() {
      try {
        const env = await CropProgressionService.getIndicesMetadata();
        this.metadata = (env && env.data) || {};
      } catch (e) {
        this.metadata = {};
      }
    },
    async init() {
      this.startLoading();
      try {
        const env = await CropProgressionService.getReport(this.reportUid);
        this.baseReport = (env && env.data) || null;
        if (this.baseReport) {
          this.reportA = this.baseReport;
          this.dateA = this.baseReport.selectedDate;
          this.prevDateA = this.dateA;
        }
        const rel = await CropProgressionService.getRelatedDates(this.reportUid);
        const d = (rel && rel.data) || {};
        this.dates = d.dates || [];
        this.generatedMap = d.generated || {};
        // pick a sensible second date (another already-generated date if any)
        const otherGenerated = Object.keys(this.generatedMap).find((dt) => dt !== this.dateA);
        if (otherGenerated) {
          this.dateB = otherGenerated;
          this.prevDateB = otherGenerated;
        }
      } finally {
        this.stopLoading();
      }
    },
    onDateChange(slot, date) {
      if (!date) return;
      // Already the base report's date → reuse it directly.
      if (this.baseReport && date === this.baseReport.selectedDate) {
        this.setSlotReport(slot, this.baseReport);
        return;
      }
      const gen = this.generatedMap[date];
      if (gen && gen.reportUid) {
        this.loadSlot(slot, gen.reportUid);
      } else {
        // Not generated yet — confirm before generating (correction #6).
        this.pendingSlot = slot;
        this.pendingDate = date;
        this.generatePrompt = true;
      }
    },
    setSlotReport(slot, report) {
      if (slot === "A") this.reportA = report;
      else this.reportB = report;
    },
    async loadSlot(slot, uid) {
      this.setLoading(slot, true);
      try {
        const env = await CropProgressionService.getReport(uid);
        const report = (env && env.data) || null;
        this.setSlotReport(slot, report);
        if (report && report.status === "GENERATING") this.pollSlot(slot, uid);
      } catch (e) {
        this.$notify({ text: this.$t("reports.cropProgression.loadFailed"), type: "error" });
      } finally {
        this.setLoading(slot, false);
      }
    },
    setLoading(slot, val) {
      if (slot === "A") this.loadingA = val;
      else this.loadingB = val;
    },
    pollSlot(slot, uid) {
      if (this.pollTimers[slot]) clearInterval(this.pollTimers[slot]);
      this.pollTimers[slot] = setInterval(async () => {
        const env = await CropProgressionService.getReport(uid);
        const report = (env && env.data) || null;
        this.setSlotReport(slot, report);
        if (!report || report.status !== "GENERATING") {
          clearInterval(this.pollTimers[slot]);
          this.pollTimers[slot] = null;
        }
      }, POLL_MS);
    },
    async confirmGenerate() {
      this.generatePrompt = false;
      const slot = this.pendingSlot;
      const date = this.pendingDate;
      if (!date) return;
      this.setLoading(slot, true);
      try {
        const env = await CropProgressionService.compareReports({
          reportUid: this.reportUid,
          dates: [date],
        });
        const report = env && env.data && env.data.reports && env.data.reports[0];
        if (report) {
          this.setSlotReport(slot, report);
          this.generatedMap = { ...this.generatedMap, [date]: { reportUid: report.reportUid, status: report.status } };
          if (report.status === "GENERATING") this.pollSlot(slot, report.reportUid);
        } else {
          this.$notify({
            text: this.reportErrorMessage({ data: env }, "reports.cropProgression.generateFailed"),
            type: "error",
          });
        }
      } catch (e) {
        this.$notify({
          text: this.reportErrorMessage(e, "reports.cropProgression.generateFailed"),
          type: "error",
        });
        this.revertSlot(slot);
      } finally {
        this.setLoading(slot, false);
        this.pendingSlot = null;
        this.pendingDate = null;
      }
    },
    cancelGenerate() {
      this.generatePrompt = false;
      this.revertSlot(this.pendingSlot);
      this.pendingSlot = null;
      this.pendingDate = null;
    },
    revertSlot(slot) {
      // Restore the previous selection without re-triggering generation.
      if (slot === "A") this.dateA = this.prevDateA;
      else this.dateB = this.prevDateB;
    },
    /* ---- slider drag ---- */
    onOverlayRects(rects) {
      const valid = (rects || []).filter(Boolean);
      // Use the top (clipped) overlay's rect; both overlays share the same bbox.
      this.overlayRect = valid.length ? valid[valid.length - 1] : null;
    },
    startDrag() {
      this.dragging = true;
      window.addEventListener("mousemove", this.onDrag);
      window.addEventListener("mouseup", this.stopDrag);
    },
    onDrag(e) {
      if (!this.dragging || !this.$refs.sliderWrap || !this.overlayRect) return;
      const wrapRect = this.$refs.sliderWrap.getBoundingClientRect();
      // Image's left edge in page coordinates, then position within image width.
      const imageLeft = wrapRect.left + this.overlayRect.left;
      let pct = ((e.clientX - imageLeft) / this.overlayRect.width) * 100;
      pct = Math.max(0, Math.min(100, pct));
      this.sliderPct = Math.round(pct);
    },
    stopDrag() {
      this.dragging = false;
      window.removeEventListener("mousemove", this.onDrag);
      window.removeEventListener("mouseup", this.stopDrag);
    },
    async downloadComparison() {
      if (!this.canDownload) return;
      this.downloading = true;
      try {
        const res = await CropProgressionService.downloadComparisonPdf({
          reportUidA: this.reportA.reportUid,
          reportUidB: this.reportB.reportUid,
        });
        this.downloadBlob(
          res.data,
          `vegetation_vigor_comparison_${this.reportA.reportUid}_${this.reportB.reportUid}.pdf`
        );
      } catch (e) {
        this.$notify({
          text: this.$t("reports.cropProgression.loadFailed"),
          type: "error",
        });
      } finally {
        this.downloading = false;
      }
    },
    goBack() {
      this.$router.push({ name: "cropProgressionView", params: { reportUid: this.reportUid } });
    },
  },
};
</script>

<style scoped>
.slider-wrap {
  position: relative;
}
.map-holder {
  position: relative;
}
.map-loading {
  position: absolute;
  inset: 0;
  z-index: 5;
  background: rgba(255, 255, 255, 0.85);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
}
.slider-divider {
  position: absolute;
  width: 2px;
  background: #fff;
  z-index: 4;
  transform: translateX(-1px);
  pointer-events: none;
  top: 0px;
  height: 100%;
}
.slider-handle {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ew-resize;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);
  pointer-events: auto;
}
</style>
